import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {usePwa} from '@/shared/lib/pwa';
import {AppButton} from '@/shared/ui/button';
import {IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * «Установить приложение» — asked on every launch, until the app is installed.
 *
 * The card on the «Ещё» screen (InstallCard) is the passive half of this: it is there
 * for somebody who goes looking. Nobody goes looking, which is why the browser tab
 * stayed a browser tab. This is the active half — it comes to the user instead.
 *
 * ## When it appears, and when it stops
 *
 * Once per launch, while the browser says the app is installable and the user has not
 * waved it away this session. Three facts do all the deciding, and none of them is a
 * counter this file keeps:
 *
 *   - **Installed → silent.** In an installed app `isStandalone` is true, and in a
 *     browser tab Chromium simply stops firing `beforeinstallprompt` once the app is
 *     installed, so `canInstall` goes false on its own.
 *   - **Uninstalled → it comes back.** The same event starts firing again, with no
 *     state anywhere to un-remember. That is the whole "до момента его удаления" clause,
 *     implemented by not implementing it.
 *   - **「Позже」 → this launch only.** The flag lives in `sessionStorage`
 *     (`shared/lib/pwa.web.ts`), so closing the sheet buys quiet for the rest of the
 *     visit and nothing more. The next launch asks again.
 *
 * ⚠️ The one platform this cannot get right is iOS Safari, which fires no install event
 * and reports no installed state to a normal tab. There, the offer is driven by
 * `needsManualInstall` (an Apple UA that is not already standalone), so somebody who
 * HAS installed the app and still browses the site in Safari keeps being offered it once
 * per visit. Apple exposes nothing that would tell us otherwise; the alternative is a
 * permanent "already installed" flag that would also survive an uninstall, which is the
 * failure this whole widget exists to avoid.
 *
 * On native `usePwa()` is inert (an APK is already installed), so this renders nothing.
 */

/** Long enough for the first screen to have painted; short enough to still read as
 *  part of opening the app rather than as an interruption of using it. */
const APPEAR_DELAY_MS = 1400;

const styles = StyleSheet.create({
  body: {gap: spacing(4)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  headText: {flex: 1, gap: 2},
  steps: {gap: spacing(2)},
  actions: {gap: spacing(2)},
});

export function InstallPrompt() {
  const {t} = useTranslation();
  const p = useTheme();
  const {
    canInstall,
    promptInstall,
    isStandalone,
    needsManualInstall,
    installDismissed,
    dismissInstall,
  } = usePwa();

  const [open, setOpen] = useState(false);

  const eligible = !isStandalone && !installDismissed && (canInstall || needsManualInstall);

  useEffect(() => {
    if (!eligible) {
      // Covers the two ways the offer can stop being true while it is on screen: the
      // user accepted it (appinstalled), or a second tab dismissed it.
      setOpen(false);
      return;
    }
    const id = setTimeout(() => setOpen(true), APPEAR_DELAY_MS);
    return () => clearTimeout(id);
  }, [eligible]);

  function close() {
    setOpen(false);
    dismissInstall();
  }

  if (!eligible) {
    return null;
  }

  return (
    <Sheet visible={open} onClose={close} testID="pwa-install-sheet">
      <View style={styles.body}>
        <View style={styles.head}>
          <IconBubble size={44} tone={p.accent} square>
            <Icon name="home" size={22} color={p.primary} />
          </IconBubble>
          <View style={styles.headText}>
            <AppText variant="title">{t('pwa.installTitle')}</AppText>
            <AppText variant="caption">{t('pwa.installHint')}</AppText>
          </View>
        </View>

        {canInstall ? null : (
          // iOS: no button can work, so the two menu items ARE the offer.
          <View style={styles.steps}>
            <AppText variant="body">{t('pwa.installIosStep1')}</AppText>
            <AppText variant="body">{t('pwa.installIosStep2')}</AppText>
          </View>
        )}

        <View style={styles.actions}>
          {canInstall ? (
            <AppButton
              title={t('pwa.installAction')}
              icon="plus"
              onPress={() => {
                // Close first: the browser's own dialog is what the user answers next,
                // and leaving our sheet under it stacks two asks on one screen. The
                // session flag goes with it — if they decline the browser's dialog,
                // the next LAUNCH asks again, this visit does not.
                close();
                void promptInstall();
              }}
              testID="pwa-install-sheet-button"
            />
          ) : null}
          <AppButton
            title={t('pwa.installLater')}
            tone="ghost"
            onPress={close}
            testID="pwa-install-sheet-later"
          />
        </View>
      </View>
    </Sheet>
  );
}
